import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, startOfMonth } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Banknote, Landmark, Plus, Trash2, Star, Check, Wallet, Clock, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/data-table';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { AppCobroCreateDialog } from '@/components/app-cobro-create-dialog';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface PayoutRow {
  id: string;
  amount: string;
  feeTotal: string;
  chargedTotal: string;
  txnCount: number;
  status: string;
  transferRef: string | null;
  paidAt: string | null;
  cutAt: string;
}
interface TxnRow {
  id: string;
  kind: 'request' | 'transaction';
  description: string;
  clientName: string | null;
  amount: string;
  method: string | null;
  status: 'pendiente' | 'procesado' | 'acreditado';
  date: string;
}
interface CobrosResp {
  summary: { pendiente: number; pagado: number; comisionTotal: number; brutoTotal: number; count: number };
  payouts: PayoutRow[];
  transactions: TxnRow[];
}
interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  accountType: string | null;
  documentNumber: string | null;
  isPrimary: boolean;
}

const gs = (v: string | number) => `Gs ${Math.round(Number(v) || 0).toLocaleString('es-PY')}`;
const fmtDate = (iso: string | null) => (iso ? format(new Date(iso), 'dd/MM/yyyy') : '—');
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

function TxnStatusBadge({ status }: { status: 'pendiente' | 'procesado' | 'acreditado' }) {
  if (status === 'acreditado')
    return (
      <Badge variant="success">
        <Check className="mr-1 size-3" /> Acreditado
      </Badge>
    );
  if (status === 'procesado') return <Badge variant="warning">Procesado</Badge>;
  return <Badge variant="secondary">Pendiente</Badge>;
}

const txnColumns: ColumnDef<TxnRow, unknown>[] = [
  {
    accessorKey: 'date',
    header: 'Fecha',
    cell: ({ row }) => <span className="whitespace-nowrap text-sm">{fmtDate(row.original.date)}</span>,
  },
  {
    id: 'detalle',
    header: 'Detalle',
    accessorFn: (r) => `${r.description} ${r.clientName ?? ''}`,
    enableSorting: false,
    cell: ({ row }) => (
      <div>
        <div className="leading-tight">{row.original.description}</div>
        {row.original.clientName && (
          <div className="text-xs text-muted-foreground">{row.original.clientName}</div>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => <TxnStatusBadge status={row.original.status} />,
  },
  {
    id: 'amount',
    header: 'Monto',
    accessorFn: (r) => Number(r.amount),
    cell: ({ row }) => (
      <span className="font-semibold tabular-nums">{gs(row.original.amount)}</span>
    ),
  },
];

const txnExport = (rows: TxnRow[]) =>
  rows.map((t) => ({
    Fecha: fmtDate(t.date),
    Detalle: t.description,
    Cliente: t.clientName ?? '',
    Estado: t.status,
    Monto: Number(t.amount),
  }));

const payoutColumns: ColumnDef<PayoutRow, unknown>[] = [
  { accessorKey: 'cutAt', header: 'Corte', cell: ({ row }) => fmtDate(row.original.cutAt) },
  {
    accessorKey: 'txnCount',
    header: 'Cobros',
    cell: ({ row }) => <span className="tabular-nums">{row.original.txnCount}</span>,
  },
  {
    id: 'feeTotal',
    header: 'Comisión',
    accessorFn: (r) => Number(r.feeTotal),
    cell: ({ row }) => <span className="tabular-nums">{gs(row.original.feeTotal)}</span>,
  },
  {
    id: 'amount',
    header: 'A recibir',
    accessorFn: (r) => Number(r.amount),
    cell: ({ row }) => <span className="font-semibold tabular-nums">{gs(row.original.amount)}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) =>
      row.original.status === 'paid' ? (
        <Badge variant="success">
          <Check className="mr-1 size-3" /> Pagado
        </Badge>
      ) : (
        <Badge variant="secondary">Pendiente</Badge>
      ),
  },
];

const payoutExport = (rows: PayoutRow[]) =>
  rows.map((p) => ({
    Corte: fmtDate(p.cutAt),
    Cobros: p.txnCount,
    Comisión: Number(p.feeTotal),
    'A recibir': Number(p.amount),
    Estado: p.status === 'paid' ? 'Pagado' : 'Pendiente',
  }));

export function AppCobrosPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();

  const [showCobro, setShowCobro] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
  const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

  const { data, isLoading } = useQuery({
    queryKey: ['app-cobros', from, to],
    queryFn: () => {
      const qs = from && to ? `?from=${from}&to=${to}` : '';
      return api<CobrosResp>(`/api/app/cobros${qs}`);
    },
    enabled: !!me,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cobros</h1>
          <p className="text-sm text-muted-foreground">
            Lo que cobrás a través de Actuo y cuánto te liquidamos en cada corte.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <DateRangePicker
            value={range}
            onChange={setRange}
            placeholder="Rango de fechas"
            align="end"
            disabled={{ after: new Date() }}
          />
          <Button className="w-full sm:w-auto" onClick={() => setShowCobro(true)}>
            <Plus className="mr-2 size-4" /> Nuevo cobro
          </Button>
        </div>
      </div>
      <AppCobroCreateDialog open={showCobro} onOpenChange={setShowCobro} />

      {/* Resumen */}
      {isLoading || !data ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            icon={<Wallet className="size-4" />}
            label="Total cobrado bruto"
            value={gs(data.summary.brutoTotal)}
            hint="Lo que pagaron tus clientes"
          />
          <StatCard
            icon={<Check className="size-4" />}
            label="Total acreditado"
            value={gs(data.summary.pagado)}
            hint="Transferido a tu cuenta"
          />
          <StatCard
            icon={<Clock className="size-4" />}
            label="Pendiente"
            value={gs(data.summary.pendiente)}
            hint="Falta acreditarte"
          />
        </div>
      )}

      {/* Separamos en tabs: el listado de transacciones puede ser largo y, si
          va todo apilado, las liquidaciones y los datos bancarios se pierden. */}
      <Tabs defaultValue="transacciones">
        <TabsList className="w-full max-w-full justify-start overflow-x-auto [&>*]:shrink-0">
          <TabsTrigger value="transacciones">
            <Receipt className="size-4" /> Transacciones
          </TabsTrigger>
          <TabsTrigger value="liquidaciones">
            <Banknote className="size-4" /> Liquidaciones
          </TabsTrigger>
          <TabsTrigger value="bancarios">
            <Landmark className="size-4" /> Datos bancarios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transacciones" className="mt-4">
          <p className="mb-3 text-sm text-muted-foreground">
            Cada cobro y en qué estado está: pendiente de pago, procesado o acreditado a tu cuenta.
          </p>
          {isLoading || !data ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <DataTable
              columns={txnColumns}
              data={data.transactions}
              searchPlaceholder="Buscar por detalle o cliente…"
              exportFileName="cobros-transacciones"
              exportRows={txnExport}
              emptyMessage="Cuando generes un cobro o recibas un pago, aparece acá con su estado."
            />
          )}
        </TabsContent>

        <TabsContent value="liquidaciones" className="mt-4">
          <p className="mb-3 text-sm text-muted-foreground">
            El detalle de cada corte y lo que te corresponde.
          </p>
          {isLoading || !data ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <DataTable
              columns={payoutColumns}
              data={data.payouts}
              searchPlaceholder="Buscar por estado…"
              exportFileName="cobros-liquidaciones"
              exportRows={payoutExport}
              emptyMessage="Cuando cobres a través de Actuo, acá vas a ver cuánto te toca en cada corte."
            />
          )}
        </TabsContent>

        <TabsContent value="bancarios" className="mt-4">
          <BankAccountsCard />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card>
      <CardContent>
        <div className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  );
}

function BankAccountsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['app-bank-accounts'],
    queryFn: () => api<{ items: BankAccount[] }>('/api/app/bank-accounts'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['app-bank-accounts'] });

  const setPrimary = useMutation({
    mutationFn: (id: string) =>
      api(`/api/app/bank-accounts/${id}/primary`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Cuenta principal actualizada.');
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e, 'Error')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/app/bank-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Cuenta eliminada.');
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al eliminar')),
  });

  const items = data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="size-4" /> Datos bancarios
          </CardTitle>
          <CardDescription>Dónde te transferimos lo que cobrás.</CardDescription>
        </div>
        <AddBankAccountDialog onDone={invalidate} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Todavía no cargaste una cuenta. Agregá una para que podamos transferirte.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{a.bankName}</span>
                    {a.isPrimary && (
                      <Badge variant="success">
                        <Star className="mr-1 size-3" /> Principal
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {a.accountNumber} · {a.accountHolder}
                    {a.accountType ? ` · ${a.accountType}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!a.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setPrimary.isPending}
                      onClick={() => setPrimary.mutate(a.id)}
                    >
                      <Star className="mr-1 size-4" /> Hacer principal
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive">
                        <Trash2 className="size-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar esta cuenta?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {a.bankName} · {a.accountNumber}. No vas a poder recibir transferencias en
                          esta cuenta.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove.mutate(a.id)}>
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AddBankAccountDialog({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountType, setAccountType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api('/api/app/bank-accounts', {
        method: 'POST',
        body: JSON.stringify({
          bankName,
          accountNumber,
          accountHolder,
          accountType: accountType || null,
          documentNumber: documentNumber || null,
        }),
      }),
    onSuccess: () => {
      toast.success('Cuenta agregada.');
      setOpen(false);
      setBankName('');
      setAccountNumber('');
      setAccountHolder('');
      setAccountType('');
      setDocumentNumber('');
      onDone();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al agregar la cuenta')),
  });

  const valid = bankName.trim() && accountNumber.trim() && accountHolder.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 size-4" /> Agregar cuenta
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar cuenta bancaria</DialogTitle>
          <DialogDescription>Donde querés recibir tus transferencias.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="bank">Banco</Label>
            <Input
              id="bank"
              placeholder="Ej: Banco Itaú"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="acc-number">Número de cuenta</Label>
            <Input
              id="acc-number"
              placeholder="Ej: 1234567"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="holder">Titular</Label>
            <Input
              id="holder"
              placeholder="Nombre del titular"
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger>
                  <SelectValue placeholder="Opcional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Caja de ahorro">Caja de ahorro</SelectItem>
                  <SelectItem value="Cuenta corriente">Cuenta corriente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="doc">CI / RUC del titular</Label>
              <Input
                id="doc"
                placeholder="Opcional"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            Agregar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
